import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Heart, Mail, Lock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
    date_of_birth: '',
    gender: '',
    profile_pic: null
  });

  const { login, register, uploadProfilePicture } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast({
          title: "🎉 Welcome back!",
          description: "Successfully logged into your self-love journey!",
          duration: 5000,
        });
        // Navigate to app using React Router (maintains context state)
        setTimeout(() => {
          navigate('/app');
        }, 1500);
      } else {
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        
        await register(formData.name, formData.email, formData.password, {
          gender: formData.gender || null,
          date_of_birth: formData.date_of_birth || null // Convert empty string to null
        });
        
        // Upload profile picture if provided
        if (formData.profile_pic) {
          try {
            await uploadProfilePicture(formData.profile_pic);
            toast({
              title: "🌟 Account Created!",
              description: "Welcome! Your profile picture has been uploaded successfully.",
              duration: 5000,
            });
          } catch (uploadError) {
            console.error('Profile picture upload failed:', uploadError);
            toast({
              title: "🌟 Account Created!",
              description: "Account created successfully, but profile picture upload failed. You can upload it later from your profile.",
              duration: 7000,
            });
          }
        } else {
          toast({
            title: "🌟 Account Created!",
            description: "Welcome to your personalized self-love journey!",
            duration: 5000,
          });
        }
        // AuthContext will handle domain redirect to app.constantloveco.com
      }
    } catch (error) {
      // Better error message handling
      let errorMessage = "Authentication failed. Please try again.";
      
      if (error.message) {
        errorMessage = typeof error.message === 'string' 
          ? error.message 
          : JSON.stringify(error.message);
      } else if (error.detail) {
        errorMessage = error.detail;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      toast({
        title: "Authentication Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 flex items-center justify-center py-4 px-4">
      <Card className="w-full max-w-sm p-4 bg-white/80 backdrop-blur-sm border-rose-200 shadow-xl">
        {/* Header */}
        <div className="text-center mb-2">
          <div className="flex justify-center mb-1">
            <img 
              src="/constant-love-logo.png" 
              alt="Constant Love™" 
              className="w-40 h-40 sm:w-48 sm:h-48 object-contain"
            />
          </div>
          <p className="text-gray-600 text-sm">
            {isLogin ? 'Welcome back to the movement' : 'Join the empowerment movement'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Name for registration */}
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="pl-10 border-rose-200 focus:ring-rose-300 focus:border-rose-300"
                />
              </div>
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="pl-10 border-rose-200 focus:ring-rose-300 focus:border-rose-300"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="pl-10 pr-10 border-rose-200 focus:ring-rose-300 focus:border-rose-300"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Forgot Password Link (login only) */}
          {isLogin && (
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-rose-600 hover:text-rose-700 font-medium"
              >
                Forgot your password?
              </Link>
            </div>
          )}

          {/* Confirm password for registration */}
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  className="pl-10 border-rose-200 focus:ring-rose-300 focus:border-rose-300"
                />
              </div>
            </div>
          )}

          {/* Birth Date for astrology (registration only) */}
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Birth Date <span className="text-gray-400">(for personalized astrology)</span>
              </label>
              <Input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleInputChange}
                className="border-rose-200 focus:ring-rose-300 focus:border-rose-300"
              />
            </div>
          )}

          {/* Gender (registration only) */}
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender <span className="text-gray-400">(optional)</span>
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </div>
          )}

          {/* Profile Picture (registration only) */}
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Picture <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFormData({...formData, profile_pic: e.target.files[0]})}
                className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF or WebP. Max file size 5MB.</p>
            </div>
          )}

          {/* Submit Button */}
          <Button 
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white font-medium py-3"
          >
            {isLoading 
              ? (isLogin ? 'Signing In...' : 'Creating Account...') 
              : (isLogin ? 'Sign In' : 'Create Account')
            }
          </Button>
        </form>

        {/* Toggle between login/register */}
        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm">
            {isLogin 
              ? "Don't have an account yet?" 
              : "Already have an account?"
            }
            {isLogin ? (
              <Link
                to="/signup"
                className="ml-2 text-rose-600 font-medium hover:text-rose-700"
              >
                Create New Account
              </Link>
            ) : (
              <button
                onClick={() => {
                  setIsLogin(true);
                  setFormData({
                    email: '',
                    password: '',
                    name: '',
                    confirmPassword: '',
                    date_of_birth: '',
                    gender: '',
                    profile_pic: null
                  });
                }}
                className="ml-2 text-rose-600 font-medium hover:text-rose-700"
              >
                Sign In
              </button>
            )}
          </p>
        </div>
      </Card>
    </div>
  );
};

export default Login;